import { useContext, useEffect } from "react";
import * as Sentry from "@sentry/react";
import { observer } from "mobx-react";
import { useTranslation } from "react-i18next";
import { UI } from "@wwimmo/ui";
import { RootStoreContext } from "src/stores/RootStore";
import { ISelectableRealestate, LOADING_TYPE } from "src/stores/RealestateStore";
import { ErpType } from "src/network/User";
import { AddRealestatesModalList } from "./list/AddRealestatesModalList";

interface IAddRealestatesModalProps {
    show: boolean;
    isCondominium: boolean;
    selectedRealestateIds: string[];
    onAdd: (realestates: ISelectableRealestate[]) => void;
    onClose: () => void;
}

// Picks realestates the user has access to. Selected realestates are hidden, the caller owns the selection.
const AddRealestatesModalBase = (props: IAddRealestatesModalProps) => {
    const { t } = useTranslation();
    const { realestateStore, uiStore, authStore } = useContext(RootStoreContext);
    const { show, isCondominium, selectedRealestateIds, onAdd, onClose } = props;

    const isIT2 = authStore.user?.erpType === ErpType.IT2;

    useEffect(() => {
        if (show) {
            realestateStore.loadSelectableRealestates(isIT2, isCondominium);
        }
    }, [show, isIT2, isCondominium, realestateStore]);

    const getEmptyListMessage = () => {
        if (realestateStore.loadingType === LOADING_TYPE.SELECTABLE_REALESTATES) {
            return t("components.add_realestates_modal.loading");
        }
        if (realestateStore.hasSelectableRealestatesLoadingError) {
            return t("components.add_realestates_modal.loading_error");
        }
        return t("components.add_realestates_modal.no_realestates_found");
    };

    return (
        <UI.Modal
            title={t("components.add_realestates_modal.title").toString()}
            show={show}
            centered={uiStore.isMobile}
            size={uiStore.isMobile ? undefined : "lg"}
            onClose={onClose}
        >
            <AddRealestatesModalList
                realestates={realestateStore.selectableRealestates}
                selectedRealestateIds={selectedRealestateIds}
                emptyListMessage={getEmptyListMessage()}
                onAdd={onAdd}
            />
        </UI.Modal>
    );
};

export const AddRealestatesModal = Sentry.withProfiler(observer(AddRealestatesModalBase));
